import { useState } from 'react';
import {
  ADDITIONAL_INTERNAL_SCHOLARSHIP_PROGRAMS,
  NON_RESIDENT_CATEGORY,
  NON_RESIDENT_HEALTH_PROGRAMS,
  NON_RESIDENT_SECTIONS,
  OVERSEAS_INTRO,
  OVERSEAS_LAST_UPDATED,
  RESIDENT_CATEGORY,
  RESIDENT_FEE_PROGRAMS,
  RESIDENT_SECTIONS,
  SCHOLARSHIP_PROGRAMS,
  SCHOLARSHIP_TABLE_FOOTNOTE,
} from './overseas-data';

function RequirementList({ items, intro }) {
  return (
    <div className="mt-4">
      {intro && <p className="mb-3 text-sm leading-relaxed text-slate-700">{intro}</p>}
      <ol className="list-decimal space-y-2 pl-6 text-sm leading-relaxed text-slate-700">
        {items.map((item, index) => (
          <li key={index}>{item}</li>
        ))}
      </ol>
    </div>
  );
}

function TableFootnotes({ footnotes }) {
  if (!footnotes?.length) return null;
  return (
    <div className="border-t border-slate-200 bg-slate-50 px-4 py-3 space-y-2">
      {footnotes.map((note) => (
        <p key={note} className="text-xs leading-relaxed text-slate-700">
          * {note}
        </p>
      ))}
    </div>
  );
}

function GenderProgramsTable({ rows, footnote }) {
  let lastCollege = '';
  const footnotes = footnote ? [footnote] : [];
  return (
    <div className="mt-6 overflow-hidden rounded-2xl border border-slate-200">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[32rem] border-collapse text-left text-sm">
          <thead>
            <tr className="bg-[#0084BD] text-white">
              <th className="px-4 py-3 font-semibold">College</th>
              <th className="px-4 py-3 font-semibold">Program</th>
              <th className="px-4 py-3 font-semibold">Gender</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => {
              const showCollege = row.college !== lastCollege;
              lastCollege = row.college;
              return (
                <tr
                  key={`${row.college}-${row.program}-${index}`}
                  className={index % 2 === 0 ? 'bg-white' : 'bg-slate-50/70'}
                >
                  <td className="px-4 py-3 align-top font-semibold text-slate-900">
                    {showCollege ? row.college : ''}
                  </td>
                  <td className="px-4 py-3 text-slate-700">{row.program}</td>
                  <td className="px-4 py-3 text-slate-700">{row.gender}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <TableFootnotes footnotes={footnotes} />
    </div>
  );
}

function AvailabilityProgramsTable({ rows, footnotes }) {
  let lastCollege = '';
  return (
    <div className="mt-6 overflow-hidden rounded-2xl border border-slate-200">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[40rem] border-collapse text-left text-sm">
          <thead>
            <tr className="bg-[#0084BD] text-white">
              <th className="px-4 py-3 font-semibold">College</th>
              <th className="px-4 py-3 font-semibold">Program</th>
              <th className="px-4 py-3 text-center font-semibold">Male Students</th>
              <th className="px-4 py-3 text-center font-semibold">Female Students</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => {
              const showCollege = row.college !== lastCollege;
              lastCollege = row.college;
              return (
                <tr
                  key={`${row.college}-${row.program}-${index}`}
                  className={index % 2 === 0 ? 'bg-white' : 'bg-slate-50/70'}
                >
                  <td className="px-4 py-3 align-top font-semibold text-slate-900">
                    {showCollege ? row.college : ''}
                  </td>
                  <td className="px-4 py-3 text-slate-700">{row.program}</td>
                  <td className="px-4 py-3 text-center text-slate-700">{row.male}</td>
                  <td className="px-4 py-3 text-center text-slate-700">{row.female}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <TableFootnotes footnotes={footnotes} />
    </div>
  );
}

function SectionCard({ section, children }) {
  return (
    <article
      id={section.id}
      className="scroll-mt-28 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8"
    >
      <h3 className="text-xl font-bold text-slate-900">{section.title}</h3>
      {section.body?.map((paragraph) => (
        <p key={paragraph} className="mt-3 text-sm leading-relaxed text-slate-700">
          {paragraph}
        </p>
      ))}
      {section.applyLink && (
        <p className="mt-2 text-sm text-slate-700">
          <a
            href={section.applyLinkHref ?? `https://${section.applyLink}`}
            target="_blank"
            rel="noreferrer"
            className="font-medium text-[#0084BD] hover:underline"
          >
            {section.applyLink}
          </a>
        </p>
      )}
      {section.requirementsTitle && (
        <h4 className="mt-6 text-base font-bold text-slate-900">{section.requirementsTitle}</h4>
      )}
      {section.requirements && (
        <RequirementList items={section.requirements} intro={section.requirementsIntro} />
      )}
      {section.scholarshipBenefitsHeading && (
        <>
          <h4 className="mt-6 text-base font-bold text-slate-900">
            {section.scholarshipBenefitsHeading}
          </h4>
          <p className="mt-2 text-sm text-slate-700">{section.scholarshipBenefitsText}</p>
          <p className="mt-2 text-sm">
            <a
              href={section.scholarshipBenefitsUrl}
              target="_blank"
              rel="noreferrer"
              className="font-medium text-[#0084BD] hover:underline break-all"
            >
              {section.scholarshipBenefitsUrl}
            </a>
          </p>
        </>
      )}
      {section.programsAvailableHeading && (
        <>
          <h4 className="mt-6 text-base font-bold text-slate-900">
            {section.programsAvailableHeading}
          </h4>
          <p className="mt-2 text-sm text-slate-700">{section.programsAvailableText}</p>
        </>
      )}
      {section.portalIntro && (
        <p className="mt-6 text-sm leading-relaxed text-slate-700">{section.portalIntro}</p>
      )}
      {section.portalUrl && (
        <p className="mt-3 text-sm font-semibold text-slate-900">
          <a
            href={section.portalUrl}
            target="_blank"
            rel="noreferrer"
            className="text-[#0084BD] hover:underline"
          >
            {section.portalLabel}
          </a>
        </p>
      )}
      {children}
    </article>
  );
}

function CategoryPanel({ categoryId, activeSectionId, onSectionChange }) {
  const sections = categoryId === 'non-resident' ? NON_RESIDENT_SECTIONS : RESIDENT_SECTIONS;
  const category = categoryId === 'non-resident' ? NON_RESIDENT_CATEGORY : RESIDENT_CATEGORY;

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <h2 className="text-2xl font-bold text-slate-900">{category.heading}</h2>
        <p className="mt-3 text-sm leading-relaxed text-slate-700">{category.intro}</p>
        {category.applyLink && (
          <p className="mt-2 text-sm">
            <a
              href={category.applyLink}
              target="_blank"
              rel="noreferrer"
              className="font-medium text-[#0084BD] hover:underline break-all"
            >
              {category.applyLink}
            </a>
          </p>
        )}
      </div>

      <div className="flex flex-wrap gap-2" role="tablist" aria-label={`${category.heading} sections`}>
        {sections.map((section) => (
          <button
            key={section.id}
            type="button"
            role="tab"
            aria-selected={activeSectionId === section.id}
            className={`rounded-full px-4 py-2 text-sm font-semibold transition ${
              activeSectionId === section.id
                ? 'bg-[#0084BD] text-white shadow-md'
                : 'bg-white text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50'
            }`}
            onClick={() => onSectionChange(section.id)}
          >
            {section.title}
          </button>
        ))}
      </div>

      {sections.map((section) =>
        activeSectionId === section.id ? (
          <SectionCard key={section.id} section={section}>
            {section.programsTableId === 'non-resident-health' && (
              <>
                <h4 className="mt-6 text-base font-bold text-slate-900">
                  {section.programsTableHeading}
                </h4>
                <AvailabilityProgramsTable
                  rows={NON_RESIDENT_HEALTH_PROGRAMS}
                  footnotes={
                    section.programsTableFootnote
                      ? [section.programsTableFootnote]
                      : undefined
                  }
                />
              </>
            )}
            {section.programsTableId === 'resident-fee' && (
              <>
                <h4 className="mt-6 text-base font-bold text-slate-900">
                  {section.programsTableHeading}
                </h4>
                <AvailabilityProgramsTable
                  rows={RESIDENT_FEE_PROGRAMS}
                  footnotes={section.programsTableFootnotes}
                />
              </>
            )}
          </SectionCard>
        ) : null,
      )}
    </div>
  );
}

export default function OverseasBachelorContent() {
  const [activeCategoryId, setActiveCategoryId] = useState('resident');
  const [nonResidentSectionId, setNonResidentSectionId] = useState('external-scholarships');
  const [residentSectionId, setResidentSectionId] = useState('internal-scholarships');

  return (
    <div className="mt-8 space-y-8 text-left">
      <section className="rounded-3xl bg-white p-6 shadow-xl sm:p-10">
        <h2 className="text-2xl font-bold text-slate-900 sm:text-3xl">{OVERSEAS_INTRO.title}</h2>
        {OVERSEAS_INTRO.paragraphs.map((paragraph) => (
          <p key={paragraph} className="mt-4 text-base leading-relaxed text-slate-700">
            {paragraph}
          </p>
        ))}
        <ol className="mt-4 list-decimal space-y-1 pl-6 text-base text-slate-700">
          {OVERSEAS_INTRO.categoryList.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ol>
      </section>

      <div className="flex flex-wrap gap-3" role="tablist" aria-label="Applicant category">
        {OVERSEAS_INTRO.categories.map((category) => (
          <button
            key={category.id}
            type="button"
            role="tab"
            aria-selected={activeCategoryId === category.id}
            className={`rounded-full px-5 py-3 text-sm font-semibold transition ${
              activeCategoryId === category.id
                ? 'bg-[#0084BD] text-white shadow-md'
                : 'bg-white text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50'
            }`}
            onClick={() => setActiveCategoryId(category.id)}
          >
            {category.label}
          </button>
        ))}
      </div>

      {activeCategoryId === 'non-resident' ? (
        <CategoryPanel
          categoryId="non-resident"
          activeSectionId={nonResidentSectionId}
          onSectionChange={setNonResidentSectionId}
        />
      ) : (
        <CategoryPanel
          categoryId="resident"
          activeSectionId={residentSectionId}
          onSectionChange={setResidentSectionId}
        />
      )}

      <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
        <h3 className="text-xl font-bold text-slate-900">Programs for Scholarships</h3>
        <GenderProgramsTable rows={SCHOLARSHIP_PROGRAMS} footnote={SCHOLARSHIP_TABLE_FOOTNOTE} />
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
        <h3 className="text-xl font-bold text-slate-900">
          Additional Programs for internal Scholarships
        </h3>
        <GenderProgramsTable rows={ADDITIONAL_INTERNAL_SCHOLARSHIP_PROGRAMS} />
      </section>

      <p className="text-center text-xs text-slate-500">
        Last updated on : {OVERSEAS_LAST_UPDATED}
      </p>
    </div>
  );
}
